import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormikProps } from 'formik';
import * as Yup from 'yup';
import { usePanel } from 'hooks/usePanel';
import {
  GetSpecialConditionsResponse,
  UpdateSpecialConditionsCommand,
  ContactDto
} from 'api/borrower';
import { borrowersAPI } from 'services/swagger-api';
import {
  specialConditionsFormConfig,
  SpecialConditionsFormValues,
  getDynamicSchemaFields
} from './specialConditionForm';
import {
  mapFormValuesToAddressDto,
  mapResponseToFormValues
} from './specialConditionsMappers';
import { useAppSelector } from 'app/hooks';
import { ReferentialKey } from 'api/referential';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';

interface UseSpecialConditionsFormProps {
  quoteId: string;
  borrowerProjectId: string;
  onSuccess?: () => void;
}

interface UseSpecialConditionsFormReturn {
  formikRef: React.RefObject<FormikProps<SpecialConditionsFormValues>>;
  initialValues: SpecialConditionsFormValues;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  validationSchema: Yup.ObjectSchema<any>;
  insuranceProjectCode: string | null;
  selectedInsuranceProjectCode: string | null;
  setSelectedInsuranceProjectCode: (code: string | null) => void;
  isLoading: boolean;
  isSubmitting: boolean;
  isFormValid: boolean;
  setIsFormValid: (isValid: boolean) => void;
  onSubmit: (values: SpecialConditionsFormValues) => Promise<void>;
}

export const useSpecialConditionsForm = ({
  quoteId,
  borrowerProjectId,
  onSuccess
}: UseSpecialConditionsFormProps): UseSpecialConditionsFormReturn => {
  const formikRef = useRef<FormikProps<SpecialConditionsFormValues>>(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [specialConditions, setSpecialConditions] =
    useState<GetSpecialConditionsResponse | null>(null);
  const [selectedInsuranceProjectCode, setSelectedInsuranceProjectCode] =
    useState<string | null>(null);
  const { setPanelActions, closePanel } = usePanel();

  const frequencyReferential = useAppSelector(selectReferentialDataByKey(ReferentialKey.MandateFrequency))

  const fetchSpecialConditions = useCallback(async () => {
    if (!quoteId) return;
    setIsLoading(true);
    try {
      const response = await borrowersAPI.getSpecialConditionsByQuoteId({
        quoteId
      });
      setSpecialConditions(response);
    } catch (error) {
      console.error('Failed to fetch special conditions:', error);
    } finally {
      setIsLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    fetchSpecialConditions();
  }, [fetchSpecialConditions]);

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: 'COMMON.CANCEL',
        submitLabel: 'COMMON.VALIDATE',
        submitDisabled: !isFormValid || isLoading || isSubmitting,
        onSubmit: () => {
          formikRef.current?.submitForm();
        }
      });
    }
  }, [formikRef, setPanelActions, isFormValid, isLoading, isSubmitting]);

  const getFrequencyCode = useCallback((frequencyId?: string) => {
    const code = frequencyReferential?.find(ref => ref.id === frequencyId)?.code
    if (!code) {
      console.error("Aucune frequence correspond a l'ID :", frequencyId)
      return 'N/A'
    }
    return code
  }, [frequencyReferential])

  const getFrequencyId = useCallback((frequencyCode: string) => {
    const id = frequencyReferential?.find(ref => ref.code === frequencyCode)?.id
    if (!id) {
      console.error("Aucune frequence correspond au code :", frequencyCode)
      return 'N/A'
    }
    return id
  }, [frequencyReferential])


  const initialValues = useMemo(
    () => mapResponseToFormValues(specialConditions, getFrequencyCode(specialConditions?.frequencyId)),
    [specialConditions, getFrequencyCode]
  );

  const validationSchema = useMemo(
    () =>
      specialConditionsFormConfig.validationSchema.shape(
        getDynamicSchemaFields(selectedInsuranceProjectCode)
      ),
    [selectedInsuranceProjectCode]
  );

  const buildContactDto = useCallback(
    (contact?: SpecialConditionsFormValues['bankContact']): ContactDto => {
      const emptyAddress = { line1: '', line2: '', zipCode: '', city: '', country: '' };
      if (!contact) return { entityName: '', entityAddress: emptyAddress };
      return {
        entityName: contact.entityName || '',
        entityAddress: mapFormValuesToAddressDto(contact.entityAddress) ?? emptyAddress,
        lastName: contact.lastName || undefined,
        firstName: contact.firstName || undefined,
        phone: contact.phone || undefined,
        email: contact.email || undefined
      };
    },
    []
  );

  const onSubmit = useCallback(
    async (values: SpecialConditionsFormValues) => {
      if (!selectedInsuranceProjectCode) {
        console.error('No insurance project code selected');
        return;
      }

      setIsSubmitting(true);
      try {
        const command: UpdateSpecialConditionsCommand = {
          borrowerProjectId,
          quoteId,
          bankAccountId: values.bankAccountId,
          frequencyId: getFrequencyId(values.frequencyCode),
          startDate: new Date(values.startDate),
          insuranceProjectCode: selectedInsuranceProjectCode,
          existingOutstanding: values.existingOutstanding,
          existingOutstandingInsurer: values.existingOutstandingInsurer,
          currentLoanNumber: values.currentLoanNumber || undefined,
          bankContact: buildContactDto(values.bankContact),
          insuranceContact: selectedInsuranceProjectCode === 'RENEG_COURTIER'
            ? buildContactDto(values.insuranceContact)
            : undefined
        };

        await borrowersAPI.processSpecialConditions({
          updateSpecialConditionsCommand: command
        });

        onSuccess?.();
        closePanel();
      } catch (error) {
        console.error('Failed to update special conditions:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [borrowerProjectId, quoteId, selectedInsuranceProjectCode, onSuccess, closePanel, getFrequencyId, buildContactDto]
  );

  const insuranceProjectCode = specialConditions?.insuranceProjectCode ?? null;

  useEffect(() => {
    setSelectedInsuranceProjectCode(insuranceProjectCode);
  }, [insuranceProjectCode]);

  return {
    formikRef,
    initialValues,
    validationSchema,
    insuranceProjectCode,
    selectedInsuranceProjectCode,
    setSelectedInsuranceProjectCode,
    isLoading,
    isSubmitting,
    isFormValid,
    setIsFormValid,
    onSubmit
  };
};
